import { cn } from "@elit-hair/elitklinik-shared";
import { Avatar, tooltipClasses } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { DateTime } from "luxon";
import { ReactNode, createElement, useMemo } from "react";
import { TbArrowRightDashed, TbBuildingHospital, TbBuildingSkyscraper, TbClock, TbStatusChange } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import TableListItem from "@/components/table/TableListItem";
import { softColors } from "@/constants/colors";
import { formatTime } from "@/helpers/date";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import {
    IHotelPlanningDepartureResponseItem,
    PatientHotelDepartureStatuses,
} from "@/schemas/hotelPlanningDepartureSchema";
import { CommonStatusItem } from "@/types/global";

function TimeSectionTooltip({
    tooltipComp,
    children,
    contentClassName,
    component,
    text,
    bg,
}: { tooltipComp: ReactNode; children: ReactNode; contentClassName?: string } & CommonStatusItem) {
    return (
        <NoMaxWidthTooltip
            arrow
            sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: "white",
                    border: "none",
                    padding: "0px !important",
                    fontSize: 12,
                    maxWidth: "200px !important",
                },
            }}
            title={tooltipComp}
        >
            <div
                className={cn("flex items-center  w-full min-w-[190px]  gap-2 group cursor-default", contentClassName)}
            >
                <Avatar
                    className=" !w-7 !h-7"
                    sx={{
                        color: text,
                        backgroundColor: bg,
                    }}
                >
                    {createElement(component(), {
                        className: "h-4 w-4",
                        color: text,
                    })}
                </Avatar>

                {children}
            </div>
        </NoMaxWidthTooltip>
    );
}

function TimeSectionTooltipComp({ tooltipInfo }: { tooltipInfo: CommonStatusItem }) {
    return <HotelStatusTooltip status={tooltipInfo} />;
}

type TimeSectionProps = {
    title: string;
    fallbackText: string;
    fallbackTooltip: {
        title: string;
        description: string;
    };
    tooltip: {
        title: string;
        description: string;
    };
    contentClassName?: string;
    time: string | null;
    username: string | null | undefined;
} & Omit<CommonStatusItem, "tooltip">;
function TimeSection(props: TimeSectionProps) {
    const { component, hover, bg, text, tooltip, fallbackText, fallbackTooltip, time, title, username } = props;
    const validTime = time && DateTime.fromISO(time);

    const tooltipInfo: CommonStatusItem = useMemo(() => {
        return {
            tooltip: validTime ? tooltip : fallbackTooltip,
            component,
            hover,
            bg,
            text,
        };
    }, [tooltip, fallbackTooltip, component, validTime, hover, bg, text]);

    return (
        <TimeSectionTooltip {...props} tooltipComp={<TimeSectionTooltipComp tooltipInfo={tooltipInfo} />}>
            {validTime ? (
                <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-medium text-slate-600  leading-none">{title}</span>
                    <span className="text-sm font-semibold leading-none">
                        {validTime && formatTime(validTime.toFormat("HH:mm"))}
                    </span>
                    <span className="text-xs text-slate-600  leading-none">{username}</span>
                </div>
            ) : (
                <div className="text-gray-700  text-xs italic ">{fallbackText}</div>
            )}
        </TimeSectionTooltip>
    );
}

const arrivalTimeSectionInfo = {
    fallbackText: "Henüz Teslim Alınmadı",
    fallbackTooltip: {
        title: "Alım Tarihi",
        description: "Yolcu henüz arabaya alınmadı",
    },
    tooltip: {
        title: "Alım Tarihi",
        description: "Yolcu arabaya alındı",
    },
    component: () => TbBuildingHospital,
    ...softColors.blue,
    contentClassName: "justify-start",
    title: "Alım",
};

const departureTimeSectionInfo = {
    fallbackText: "Henüz Otele Bırakılmadı",
    fallbackTooltip: {
        title: "Bırakış Tarihi",
        description: "Yolcu henüz bırakış yapılmadı",
    },
    tooltip: {
        title: "Bırakış Tarihi",
        description: "Yolcu bırakış yapıldı",
    },
    component: () => TbBuildingSkyscraper,
    ...softColors.green,
    contentClassName: "justify-start",
    title: "Bırakış",
};

function HotelDepartureDateRangeContent({
    row: { status, formattedWaitingTime, enteredAt, enteredBy, exitedAt, exitedBy },
    isPastDate,
}: {
    row: IHotelPlanningDepartureResponseItem;
    isPastDate: boolean;
}) {
    // History: a row still PENDING on a past day was never transferred — no live waiting badge.
    if (status === PatientHotelDepartureStatuses.PENDING && isPastDate) {
        return (
            <TableListItem className={["h-full"]}>
                <TbClock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
                    Transfer yapılmadı
                </span>
            </TableListItem>
        );
    }

    if (status === PatientHotelDepartureStatuses.PENDING) {
        const isFutureWaiting = !!formattedWaitingTime?.includes("sonra");

        return (
            <TableListItem className={["animate-pulse h-full"]}>
                <div className="w-1.5 h-1.5 rounded-full bg-gray-500 dark:bg-gray-400" />
                <TbClock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-800 dark:text-red-200 truncate">Transfer bekliyor</span>
                <div className="flex items-center gap-2">
                    <span
                        className={cn(
                            "text-xs px-2 py-1 border rounded-full font-medium",
                            isFutureWaiting
                                ? "border-amber-500 text-amber-700 bg-amber-50 dark:border-amber-400 dark:text-amber-300 dark:bg-amber-950"
                                : "border-gray-700"
                        )}
                    >
                        {formattedWaitingTime || ""} bekliyor
                    </span>
                </div>
            </TableListItem>
        );
    }

    return (
        <TableListItem className={["gap-1 px-1 grid grid-cols-[1fr_25px_1fr]  items-center"]}>
            <TimeSection {...arrivalTimeSectionInfo} time={enteredAt} username={enteredBy?.userName} />
            <TbArrowRightDashed fontSize={24} className="!shrink-0 !grow-0 text-slate-600" />
            <TimeSection {...departureTimeSectionInfo} time={exitedAt} username={exitedBy?.userName} />
        </TableListItem>
    );
}

function HotelDepartureDateRangeCell({
    row,
    isPastDate,
}: {
    row: IHotelPlanningDepartureResponseItem;
    isPastDate: boolean;
}) {
    return (
        <div className="flex items-center h-full py-1 ">
            <HotelDepartureDateRangeContent row={row} isPastDate={isPastDate} />
        </div>
    );
}

export const HotelDepartureDateRangeColumn = ({
    t,
    isPastDate = false,
}: {
    t: TFunction;
    isPastDate?: boolean;
}): GridColDef<IHotelPlanningDepartureResponseItem> => {
    return {
        field: "transferInfo",
        headerName: t("transferInfo"),
        minWidth: 450,
        filterable: false,
        flex: 1.5,
        valueGetter: (_val, row) => row.status,
        renderHeader: (params) => <TableHeader Component={TbStatusChange} title={params.colDef.headerName!} />,
        renderCell: ({ row }) => <HotelDepartureDateRangeCell row={row} isPastDate={isPastDate} />,
    };
};
export default HotelDepartureDateRangeColumn;
